import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import ProductDetail from "../productdetail/ProductDetail";
import ProductGallery from "../productquickview/ProductGallery";
import { getApiData, getApiErrors } from "../../../frontend/api/http";
import type { CartLineQuickEditPayload } from "../../types/CartType";

export interface ProductQuickAddToCartProps {
    productSlug: string;
    productName?: string;
    cartLineQuickEdit?: CartLineQuickEditPayload | null;
}

const ProductQuickAddToCart: React.FC<ProductQuickAddToCartProps> = ({
    productSlug,
    cartLineQuickEdit,
}) => {
    const [loading, setLoading] = useState(true);
    const [productData, setProductData] = useState<any>(null);
    const [error, setError] = useState<string>("");
    const { t } = useTranslation();

    // Gallery state to sync with ProductDetail selections
    const [selectedOptionValues, setSelectedOptionValues] = useState<number[]>(
        []
    );
    const [selectedUomId, setSelectedUomId] = useState<number | undefined>();
    const [selectedVariantId, setSelectedVariantId] = useState<
        number | undefined
    >();
    const [galleryFilterByUser, setGalleryFilterByUser] = useState(false);

    useEffect(() => {
        setGalleryFilterByUser(false);
        setSelectedOptionValues([]);
        setSelectedUomId(undefined);
        setSelectedVariantId(undefined);
    }, [productSlug]);

    useEffect(() => {
        const loadProductData = async () => {
            try {
                setLoading(true);

                // Call API to get product detail (same endpoint as quick view)
                const response = await fetch(
                    `/product/detailajax?slug=${encodeURIComponent(productSlug)}`
                );
                const result = await response.json();
                const payload = getApiData<any>(result);

                if (!payload?.product) {
                    throw new Error(
                        getApiErrors(result)[0] || t("product.quick_add.load_error")
                    );
                }

                setProductData(payload);
            } catch (err) {
                console.error("Error loading product data:", err);
                setError(
                    err instanceof Error
                        ? err.message
                        : t("product.quick_add.load_error")
                );
            } finally {
                setLoading(false);
            }
        };

        if (productSlug) {
            loadProductData();
        }
    }, [productSlug, t]);

    // Listen for product option changes from ProductDetail component
    useEffect(() => {
        const handleOptionChange = (event: CustomEvent) => {
            const {
                selectedOptionValues: optionValues,
                selectedVariant,
                selectedUomId: uomId,
                applyGalleryFilter,
            } = event.detail || {};

            if (applyGalleryFilter === true) {
                setGalleryFilterByUser(true);
            }

            if (optionValues) {
                const optionValueIds = optionValues.map(
                    (opt: any) => opt.option_value_id
                );
                setSelectedOptionValues(optionValueIds);
            } else {
                setSelectedOptionValues([]);
            }

            if (selectedVariant) {
                setSelectedVariantId(selectedVariant.id);
            } else {
                setSelectedVariantId(undefined);
            }

            if (uomId) {
                setSelectedUomId(uomId);
            } else {
                setSelectedUomId(undefined);
            }
        };

        window.addEventListener(
            "product:option-changed",
            handleOptionChange as any
        );

        return () => {
            window.removeEventListener(
                "product:option-changed",
                handleOptionChange as any
            );
        };
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                    {t("product.quick_add.loading")}
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-500 mb-4">
                    <svg
                        className="w-16 h-16 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t("product.quick_add.error")}
                </h3>
                <p className="text-gray-600">{error}</p>
            </div>
        );
    }

    if (!productData || !productData.product) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t("product.quick_add.not_found")}
                </h3>
                <p className="text-gray-600">
                    {t("product.quick_add.not_exist", { slug: productSlug })}
                </p>
            </div>
        );
    }

    const {
        product,
        productUrl: product_url,
        productProps: product_props,
        productSettings: product_settings,
        gallery,
        websitePricePolicyId: website_price_policy_id,
        activePricePolicyId: active_price_policy_id,
    } = productData;

    return (
        <div className="product-quick-add-to-cart">
            <div className="grid grid-cols-1 md:grid-cols-10 gap-6">
                {/* Product Gallery - Left Column (4/10) */}
                <div className="product-images md:col-span-4">
                    <ProductGallery
                        gallery={gallery}
                        productName={product.name}
                        productSlug={productSlug}
                        selectedOptionValues={
                            galleryFilterByUser ? selectedOptionValues : []
                        }
                        selectedUomId={galleryFilterByUser ? selectedUomId : undefined}
                        selectedVariantId={
                            galleryFilterByUser ? selectedVariantId : undefined
                        }
                    />
                </div>

                {/* Product Info - Right Column (6/10) */}
                <div className="product-info md:col-span-6">
                    {/* Product Name */}
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                            {product.name}
                        </h2>
                    </div>

                    {/* Product Detail Component */}
                    <ProductDetail
                        productProps={product_props}
                        productSettings={product_settings}
                        websitePricePolicyId={website_price_policy_id}
                        selectedPricePolicyId={active_price_policy_id}
                        isQuickView={true}
                        isQuickAddToCart={true}
                        productUrl={product_url || ""}
                        cartLineQuickEdit={cartLineQuickEdit ?? null}
                    />

                    {/* View Detail Link */}
                    <div className="mt-10 border-t border-gray-200 pt-4">
                        <a
                            href={`${product_url || "#"}`}
                            className="hover:underline hover:text-brand"
                        >
                            {t("product.quick_add.view_detail")}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductQuickAddToCart;
